"use client";

import { useState } from "react";
import { Mail } from "lucide-react";
import { TurnstileWidget } from "./TurnstileWidget";

export function GuideCaptureForm() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);

  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    if (!consent) {
      setError("Please tick the consent box to continue.");
      return;
    }
    if (!turnstileToken) {
      setError("Complete the verification challenge to continue.");
      return;
    }
    setError("");

    setIsPending(true);
    try {
      const res = await fetch("/api/email-capture", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          pageUrl: window.location.href,
          turnstileToken,
          consent,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? "Something went wrong.");
      }

      setIsSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Something went wrong. Please try again.",
      );
    } finally {
      setTurnstileToken("");
      setTurnstileResetKey((current) => current + 1);
      setIsPending(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="relative overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-8 text-center shadow-[0_12px_28px_rgba(28,28,30,0.05)] animate-in fade-in zoom-in duration-500">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-md bg-white text-emerald-700 shadow-sm">
          <Mail size={24} aria-hidden="true" />
        </div>
        <p className="mt-4 text-lg font-semibold text-emerald-900">You&apos;re subscribed!</p>
        <p className="mt-2 text-sm text-emerald-800">
          You&apos;ll receive new pricing reports at <strong className="font-semibold">{email}</strong>.
        </p>
      </div>
    );
  }

  return (
    <form
      className="relative overflow-hidden rounded-xl border border-[var(--color-border)] bg-white p-6 shadow-[0_12px_28px_rgba(28,28,30,0.05)] transition-[transform,box-shadow] hover:shadow-[0_18px_36px_rgba(28,28,30,0.08)]"
      onSubmit={handleSubmit}
    >
      <div className="relative z-10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="mt-0.5 flex h-12 w-12 items-center justify-center rounded-md bg-emerald-50 text-[var(--color-accent)] shadow-sm">
              <Mail size={20} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="section-label">Stay updated</p>
              <p className="mt-1 text-sm text-[var(--color-muted)]">
                New procedure cost data, city reports, and pricing updates. No general marketing newsletters.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <label className="sr-only" htmlFor="guide-email">
            Email address
          </label>
          <input
            id="guide-email"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Your email address"
            className="w-full flex-1 rounded-md border border-[var(--color-border)] bg-slate-50 px-5 py-3.5 text-[var(--color-primary)] outline-none transition-colors focus:border-[var(--color-accent)] focus:bg-white focus:ring-4 focus:ring-emerald-50"
            disabled={isPending}
            aria-invalid={!!error}
            aria-describedby={error ? "guide-email-error" : undefined}
          />
          <button
            type="submit"
            className="homepage-subscribe-button inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-2xl border border-emerald-700/50 bg-emerald-700 bg-[var(--color-cta)] px-8 py-3 text-base font-bold tracking-wide text-white shadow-lg shadow-emerald-500/20 transition-[background-color,box-shadow] duration-300 hover:bg-emerald-700 hover:bg-[var(--color-cta-hover)] hover:shadow-xl hover:shadow-emerald-500/30 active:bg-emerald-700 active:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-transparent disabled:bg-emerald-300 disabled:text-white/70 disabled:shadow-none sm:w-auto"
            disabled={isPending || !email || !consent || !turnstileToken}
          >
            {isPending ? "Sending..." : "Subscribe"}
          </button>
        </div>
        <label htmlFor="guide-capture-consent" className="mt-3 flex items-start gap-2 text-xs text-[var(--color-muted)]">
          <input
            id="guide-capture-consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 shrink-0"
            required
          />
          <span>
            Email me CostDoc pricing updates. I have read the{" "}
            <a href="/privacy" className="underline hover:text-[var(--color-primary)]">Privacy Policy</a>{" "}
            and can unsubscribe any time.
          </span>
        </label>
        <TurnstileWidget
          action="guide-capture"
          resetKey={turnstileResetKey}
          onTokenChange={setTurnstileToken}
          className="mt-3"
        />
        {error && (
          <p id="guide-email-error" role="alert" className="mt-2 text-sm font-medium text-red-600">
            {error}
          </p>
        )}
      </div>
    </form>
  );
}
